import { useState, useEffect, useCallback } from 'react';
import { hotelApi } from '../../hotelApi';

const STATUS_STYLE = {
  pending:   { bg: '#fef3c7', border: '#fbbf24', color: '#b45309', label: 'Pending' },
  preparing: { bg: '#fff3e0', border: '#fb8c00', color: '#e65100', label: 'Preparing' },
  served:    { bg: '#e6f4ea', border: '#34a853', color: '#137333', label: 'Served' },
};

function groupByOrder(items) {
  const map = {};
  items.forEach(item => {
    const key = item.order_id;
    if (!map[key]) map[key] = { orderId: item.order_id, orderNo: item.order_no, tableNo: item.table_no,
      section: item.section, orderCreated: item.order_created, items: [] };
    map[key].items.push(item);
  });
  return Object.values(map);
}

export default function HotelKitchen({ role }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const load = useCallback(() => {
    hotelApi.getKitchen()
      .then(d => { setItems(d || []); setLastRefresh(new Date()); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const updateStatus = async (itemId, status) => {
    setUpdating(u => ({ ...u, [itemId]: true }));
    try {
      await hotelApi.updateKitchenItem(itemId, { status });
      load();
    } finally {
      setUpdating(u => ({ ...u, [itemId]: false }));
    }
  };

  const elapsed = (t) => {
    if (!t) return '';
    const ms = Date.now() - new Date(t).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ${m % 60}m ago`;
  };

  const groups = groupByOrder(items);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading kitchen…</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#0d3321' }}>
            Kitchen Display Screen
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
            Last refreshed: {lastRefresh.toLocaleTimeString('en-IN')} · Auto-refreshes every 30s
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 10, padding: '4px 12px',
            fontSize: 13, fontWeight: 600 }}>
            {items.length} pending item{items.length !== 1 ? 's' : ''}
          </span>
          <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 13 }}
            onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#137333', marginBottom: 8 }}>Kitchen is clear!</div>
          <div style={{ fontSize: 14, color: '#888' }}>All orders have been served.</div>
        </div>
      )}

      {groups.map(group => {
        const allServed = group.items.every(i => i.status === 'served');
        return (
          <div key={group.orderId}
            style={{ background: allServed ? '#e6f4ea' : '#fff', borderRadius: 14,
              border: `2px solid ${allServed ? '#34a853' : '#e8eaed'}`,
              marginBottom: 16, overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,.07)' }}>
            {/* Order header */}
            <div style={{ padding: '12px 16px', background: allServed ? '#e6f4ea' : '#f8faf8',
              borderBottom: `1px solid ${allServed ? '#34a85330' : '#e8eaed'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontWeight: 800, fontSize: 16, color: allServed ? '#137333' : '#0d3321' }}>
                  {group.tableNo ? `Table ${group.tableNo}` : 'Table —'}
                </span>
                {group.section && <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{group.section}</span>}
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#888', marginLeft: 12 }}>#{group.orderNo}</span>
              </div>
              <div style={{ fontSize: 12, color: '#888' }}>{elapsed(group.orderCreated)}</div>
              {allServed && (
                <div style={{ background: '#137333', color: '#fff', borderRadius: 20, padding: '4px 14px',
                  fontSize: 13, fontWeight: 700 }}>
                  ✅ All Ready
                </div>
              )}
            </div>

            {/* Items */}
            <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {group.items.map(item => {
                const ss = STATUS_STYLE[item.status] || STATUS_STYLE.pending;
                return (
                  <div key={item.id}
                    style={{ background: ss.bg, border: `2px solid ${ss.border}`,
                      borderRadius: 12, padding: '12px 16px', minWidth: 200, flex: '1 1 200px' }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: ss.color, marginBottom: 4 }}>
                      {item.item_name}
                    </div>
                    <div style={{ fontSize: 13, color: '#5f6368', marginBottom: 4 }}>
                      Qty: <strong>{item.quantity}</strong>
                    </div>
                    {item.notes && (
                      <div style={{ fontSize: 12, color: '#b45309', background: '#fef9c3',
                        borderRadius: 6, padding: '3px 8px', marginBottom: 8 }}>
                        📝 {item.notes}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      {item.status === 'pending' && (
                        <button
                          disabled={updating[item.id]}
                          onClick={() => updateStatus(item.id, 'preparing')}
                          style={{ background: '#fb8c00', color: '#fff', border: 'none', borderRadius: 8,
                            padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          {updating[item.id] ? '…' : '🔥 Start Preparing'}
                        </button>
                      )}
                      {item.status === 'preparing' && (
                        <button
                          disabled={updating[item.id]}
                          onClick={() => updateStatus(item.id, 'served')}
                          style={{ background: '#137333', color: '#fff', border: 'none', borderRadius: 8,
                            padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          {updating[item.id] ? '…' : '✅ Mark Served'}
                        </button>
                      )}
                      {item.status === 'served' && (
                        <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 8,
                          padding: '5px 12px', fontSize: 12, fontWeight: 600 }}>
                          ✅ Served
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
